import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import PropTypes from "prop-types";

const useStyles = makeStyles((theme) => ({
  tabsContainer: {
    display: "inline-flex",
    border: "1px solid #DEE2E6",
    borderRadius: "22px",
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    marginBottom: theme.spacing(3),
    position: "relative",
    zIndex: 1,
    padding: "2px",
  },
  tab: {
    padding: "2px 16px",
    height: "44px",
    cursor: "pointer",
    userSelect: "none",
    transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
    borderRight: "1px solid #DEE2E6",
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
    boxSizing: "border-box",
    "&:last-child": {
      borderRight: "none",
    },
    "&:hover": {
      backgroundColor: "#F8F9FA",
    },
    "& *": {
      pointerEvents: "none",
    },
  },
  firstTab: {
    borderTopLeftRadius: "20px",
    borderBottomLeftRadius: "20px",
  },
  lastTab: {
    borderTopRightRadius: "20px",
    borderBottomRightRadius: "20px",
  },
  onlyTab: {
    borderRadius: "20px",
  },
  activeTab: {
    backgroundColor: "#495057",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#495057",
    },
  },
  inactiveTab: {
    backgroundColor: "#FFFFFF",
    color: "#495057",
  },
  tabLabel: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    fontStyle: "normal",
    lineHeight: "100%",
    margin: 0,
    textAlign: "center",
    verticalAlign: "middle",
  },
}));

/**
 * NavigationTabs Component
 * 
 * A reusable navigation tabs component that displays multiple tabs in a single
 * rounded rectangular container. Active tab is highlighted with dark background.
 * 
 * @param {Array} tabs - Array of tab objects with { id, label } structure
 * @param {string} defaultTab - Default active tab ID
 * @param {string} activeTab - Controlled active tab ID (overrides defaultTab)
 * @param {Function} onTabChange - Callback function called when tab changes (tabId) => void
 * @param {string} className - Additional CSS classes
 */
const NavigationTabs = ({ tabs, defaultTab, activeTab: controlledActiveTab, onTabChange, className }) => {
  const classes = useStyles();
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  const handleTabClick = (tabId) => {
    console.log("Tab clicked:", tabId, "Current active:", activeTab);
    if (tabId !== activeTab) {
      if (controlledActiveTab === undefined) {
        setInternalActiveTab(tabId);
      }
      if (onTabChange) {
        onTabChange(tabId);
      }
    }
  };

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <div className={`${classes.tabsContainer} ${className || ""}`}>
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.id;
        const isFirst = index === 0;
        const isLast = index === tabs.length - 1;
        const isOnly = tabs.length === 1;
        let tabClass = `${classes.tab} ${
          isActive ? classes.activeTab : classes.inactiveTab
        }`;
        
        // Always apply border radius based on position (not active state)
        if (isOnly) {
          tabClass += ` ${classes.onlyTab}`;
        } else if (isFirst) {
          tabClass += ` ${classes.firstTab}`;
        } else if (isLast) {
          tabClass += ` ${classes.lastTab}`;
        }

        return (
          <div
            key={tab.id || index}
            className={tabClass}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log("Tab div clicked:", tab.id);
              handleTabClick(tab.id);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleTabClick(tab.id);
              }
            }}
            aria-label={`${tab.label} tab`}
          >
            <Typography className={classes.tabLabel}>
              {tab.label}
            </Typography>
          </div>
        );
      })}
    </div>
  );
};

NavigationTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  defaultTab: PropTypes.string,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  className: PropTypes.string,
};

NavigationTabs.defaultProps = {
  defaultTab: null,
  activeTab: undefined,
  onTabChange: null,
  className: "",
};

export default NavigationTabs;

